import { useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { SpinningLoader, styles, Text } from '@frontend/design-system';
import { INSURANCE_STATUS_MAPPING } from '../constants';
import { formatDateForDisplay } from '../helpers';
import { useGetVerifiedInsuranceDetailsList } from '../hooks/use-get-verified-insurance-details-list';
import { InsuranceStatusChip } from '../insurance-status-chip';
import { DetailText } from './detail-text';

interface Props {
  personId: string;
  locationId: string;
}

export const VerifiedInsuranceDetailsList = ({ personId, locationId }: Props) => {
  const { t } = useTranslation('insuranceDetails');
  const {
    isLoading,
    birthDate,
    effectiveDate,
    firstName,
    groupNumber,
    lastName,
    payerId,
    payerName,
    planType,
    verificationDate,
    status,
  } = useGetVerifiedInsuranceDetailsList({
    personId,
    locationId,
  });

  const statusInfo = useMemo(() => {
    return INSURANCE_STATUS_MAPPING.get(status || '');
  }, [status]);

  return (
    <>
      {isLoading ? (
        <div css={styles.flexCenter}>
          <SpinningLoader size='medium' />
        </div>
      ) : (
        <dl css={listStyle}>
          <div>
            <Text as='dt'>{t('Payer ID')}</Text>
            <DetailText value={payerId} />
          </div>
          <div>
            <Text as='dt'>{t('Status')}</Text>
            {statusInfo ? <InsuranceStatusChip statusInfo={statusInfo} /> : ''}
          </div>
          <div>
            <Text as='dt'>{t('Payer Name')}</Text>
            <DetailText value={payerName} />
          </div>
          <div>
            <Text as='dt'>{t('Verification Date')}</Text>
            <DetailText value={verificationDate} />
          </div>
          <div>
            <Text as='dt'>{t('Group Number')}</Text>
            <DetailText value={groupNumber} />
          </div>
          <div>
            <Text as='dt'>{t('Effective Date')}</Text>
            <DetailText value={effectiveDate ? formatDateForDisplay(effectiveDate, 'DD MMMM YYYY') : ''} />
          </div>
          <div>
            <Text as='dt'>{t('Plan Type')}</Text>
            <DetailText value={planType} />
          </div>
          <div>
            <Text as='dt'>{t('First Name')}</Text>
            <DetailText value={firstName} required />
          </div>
          <div>
            <Text as='dt'>{t('Last Name')}</Text>
            <DetailText value={lastName} required />
          </div>
          <div>
            <Text as='dt'>{t('Birthdate')}</Text>
            <DetailText value={birthDate ? formatDateForDisplay(birthDate, 'DD MMMM YYYY') : ''} required />
          </div>
        </dl>
      )}
    </>
  );
};

const listStyle = css`
  margin: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${theme.spacing(2)};
  grid-auto-flow: dense;
  > div {
    display: flex;
    flex-direction: column;

    dt {
      margin-right: ${theme.spacing(4)};
      font-size: ${theme.fontSize(12)};
      margin-bottom: ${theme.spacing(0.5)};
      color: ${theme.font.colors.subdued};
    }
  }
`;
